import * as React from "react";
import { Autocomplete, TextField, InputLabel } from "@mui/material";
import Select from "apollo-react/components/Select";
import MenuItem from "apollo-react/components/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "apollo-react-icons/Info";
import IconButton from "apollo-react/components/IconButton";
import Tooltip from "apollo-react/components/Tooltip";
import classNames from "classnames";
import { cloneDeep } from "lodash";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { OpportunityApi } from "../../api/opportunity-api";
import { Option } from "./types";
// import { DefaultTabOptions } from "./constants";
import { getBidType } from "../../utils/answerUtils";

const DefaultTabOptions = [];

export const SelectLabel = ({ label, tooltipText }) => (
  <div className="select-tooltip-container">
    {label}
    <Tooltip variant="dark" title={tooltipText} placement="top-start" classes={{ popper: "select-tooltip-popper" }}>
      <IconButton color="primary" size="small">
        <InfoIcon />
      </IconButton>
    </Tooltip>
  </div>
);

export default function Filters({ onOppNumberChange, onBidChange, onTabChange, onBidDetailsChange, bid, tab }) {
  const [oppNoKeyword, setOppNoKeyword] = React.useState<string>("");
  const [bidOptions, setBidOptions] = React.useState<Array<Option>>([]);
  const [tabOptions, setTabOptions] = React.useState<Array<Option>>(DefaultTabOptions);

  // @ts-ignore
  const { data, isFetching } = OpportunityApi.useGetOppNumbersByKeywordQuery(oppNoKeyword, {
    skip: oppNoKeyword.length < 3,
  });

  // @ts-ignore
  const { data: bidDetails, isFetching: bidLoading } = OpportunityApi.useGetBidByIdQuery(bid, {
    skip: bid === "",
  });

  React.useEffect(() => {
    onBidDetailsChange(bidDetails || null);
    // compute and update tab options
    let finalTabOptions = cloneDeep(DefaultTabOptions);
    // @ts-ignore
    if (bidDetails && bidDetails.proposal && Array.isArray(bidDetails.proposal.approvals)) {
      if (
        // @ts-ignore
        bidDetails.proposal.approvals.filter(
          (approvalSection: any) =>
            approvalSection.ApprovalSectionLeftQuestions.length > 0 ||
            approvalSection.ApprovalSectionRightQuestions.length > 0
        ).length > 0
      ) {
        finalTabOptions.push({ label: "Approvals", value: "Approvals" });
      }
    }
    // @ts-ignore
    if (bidDetails && bidDetails.proposal && Array.isArray(bidDetails.proposal.customUnityTabs)) {
      // @ts-ignore
      const customTabsMap = bidDetails.proposal.customUnityTabs
        .filter((customTabSection) => customTabSection.UnityTabSectionQuestions.length > 0)
        .reduce((acc, customTabSection) => {
          acc[customTabSection.UnityTabId] = {
            title: customTabSection.UnityTabTitle,
          };
          return acc;
        }, {});

      Object.entries(customTabsMap).forEach(([tabId, tabInfo]: any[]) => {
        finalTabOptions.push({ label: tabInfo.title, value: tabId });
      });
    }

    // update state if diff from default option
    if (finalTabOptions.length !== DefaultTabOptions.length) {
      setTabOptions(finalTabOptions);
      onTabChange(finalTabOptions[0].value);
    }
  }, [bidDetails]);

  // @ts-ignore
  function handleOppNumberInputChange(event: React.SyntheticEvent, newOppNoKeyword: string) {
    setOppNoKeyword(newOppNoKeyword);
  }

  // @ts-ignore
  function handleOppNumberChange(event: React.SyntheticEvent, newOppNumber: any) {
    setOppNoKeyword("");
    if (newOppNumber) {
      const formattedBids = newOppNumber.data.map((bidInfo, idx) => {
        return {
          label: `${getBidType(bidInfo.bidType)} ${bidInfo.bidNo}${idx === 0 ? " (Current Bid)" : ""}`,
          value: bidInfo.proposalId,
        };
      });
      setBidOptions(formattedBids);
      onOppNumberChange(newOppNumber?.opportunityNumber, formattedBids[0].value);
    } else {
      setBidOptions([]);
      onBidChange("");
      onTabChange("");
      onBidDetailsChange(null);
    }
  }

  // @ts-ignore
  function handleBidChange(event) {
    onBidChange(event.target.value);
  }

  function handleTabChange(event) {
    onTabChange(event.target.value);
  }

  return (
    <div className="filter-container">
      <div style={{ width: "100%" }}>
        <InputLabel>Opportunity Number</InputLabel>
      </div>
      <Autocomplete
        disablePortal
        className={classNames(["input-filter", "opp-number-autocomplete"])}
        // @ts-ignore
        options={data || []}
        isOptionEqualToValue={(option: any, value: any) => option.opportunityNumber === value.opportunityNumber}
        getOptionLabel={(option: any) => option.opportunityNumber || ""}
        onInputChange={handleOppNumberInputChange}
        renderInput={(params) => <TextField {...params} placeholder="Search" />}
        popupIcon={<SearchIcon />}
        fullWidth
        loading={isFetching}
        open={oppNoKeyword.length >= 3}
        onChange={handleOppNumberChange}
      />
      <Select
        canDeselect={false}
        label={
          <SelectLabel
            label="Bid"
            tooltipText="By default questions & answers of
                        Current Bid will be shown. You can
                        select previous Bids to view the details."
          />
        }
        value={bid}
        onChange={handleBidChange}
        placeholder="Select Bid"
        fullWidth
        disabled={bid === ""}
      >
        {bidOptions.map((bidOption, ind) => (
          <MenuItem key={`bid-option-${ind}`} value={bidOption.value}>
            {bidOption.label}
          </MenuItem>
        ))}
      </Select>
      <Box sx={{ display: "flex", alignItems: "flex-end", width: "100%", mb: ".5rem" }}>
        <Select
          canDeselect={false}
          label={
            <SelectLabel
              label="Unity Tab"
              tooltipText="Select the Unity Tab to see questions &
                            answers for the bid and opportunity"
            />
          }
          value={tab}
          onChange={handleTabChange}
          placeholder="Select Tab"
          fullWidth
          disabled={tab === "" || bidLoading}
        >
          {tabOptions.map((tabOption, ind) => (
            <MenuItem key={`tab-option-${ind}`} value={tabOption.value}>
              {tabOption.label}
            </MenuItem>
          ))}
        </Select>
        {bidLoading ? <CircularProgress size={20} sx={{ m: "20px" }} /> : null}
      </Box>
    </div>
  );
}
